import React, { useState, useRef, useEffect } from 'react';
import { 
  FileText, 
  Receipt,
  CreditCard,
  FileCheck,
  DollarSign,
  Plus,
  ChevronDown
} from 'lucide-react';
import Button from '../../../components/ui/Button';
import TaxInvoiceForm from '../sales/components/TaxInvoiceForm';

const Purchases = () => {
  const [activeTab, setActiveTab] = useState('purchase-invoices');
  const [isCreateDropdownOpen, setIsCreateDropdownOpen] = useState(false);
  const [isInvoiceFormOpen, setIsInvoiceFormOpen] = useState(false);
  const [invoiceFormType, setInvoiceFormType] = useState('purchase-invoice');
  const [refreshKey, setRefreshKey] = useState(0);
  const dropdownRef = useRef(null);

  const tabs = [
    { id: 'purchase-invoices', label: 'Purchase Invoices', icon: FileText },
    { id: 'purchase-orders', label: 'Purchase Orders', icon: FileCheck },
    { id: 'debit-notes', label: 'Debit Notes', icon: CreditCard },
    { id: 'voucher-payments', label: 'Voucher Payments', icon: Receipt },
    { id: 'salary-payments', label: 'Salary Payments', icon: DollarSign },
    { id: 'surveyor-expenses', label: 'Surveyor Expenses', icon: DollarSign }
  ];

  const createOptions = [
    { id: 'purchase-invoice', label: 'Purchase Invoice', icon: FileText },
    { id: 'purchase-order', label: 'Purchase Order', icon: FileCheck },
    { id: 'debit-note', label: 'Debit Note', icon: CreditCard },
    { id: 'voucher-payment', label: 'Voucher Payment', icon: Receipt },
    { id: 'salary-payment', label: 'Salary Payment', icon: DollarSign }
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsCreateDropdownOpen(false);
      }
    };

    if (isCreateDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCreateDropdownOpen]);

  const handleCreateOption = (optionId) => {
    setIsCreateDropdownOpen(false);
    // Open the form for all document types
    setInvoiceFormType(optionId);
    setIsInvoiceFormOpen(true);
  };

  const handleInvoiceFormSave = () => {
    setIsInvoiceFormOpen(false);
    if (activeTab === 'purchase-invoices') {
      setRefreshKey((prev) => prev + 1);
    } else {
      setRefreshKey((prev) => prev + 1);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'purchase-invoices':
        const PurchaseInvoices = React.lazy(() => import('./components/PurchaseInvoices'));
        return (
          <React.Suspense fallback={<div className="p-6">Loading purchase invoices...</div>}>
            <PurchaseInvoices key={refreshKey} />
          </React.Suspense>
        );

      case 'purchase-orders':
        const PurchaseOrders = React.lazy(() => import('./components/PurchaseOrders'));
        return (
          <React.Suspense fallback={<div className="p-6">Loading purchase orders...</div>}>
            <PurchaseOrders key={refreshKey} />
          </React.Suspense>
        );

      case 'debit-notes':
        const DebitNotes = React.lazy(() => import('./components/DebitNotes'));
        return (
          <React.Suspense fallback={<div className="p-6">Loading debit notes...</div>}>
            <DebitNotes key={refreshKey} />
          </React.Suspense>
        );

      case 'voucher-payments':
        const VoucherPayments = React.lazy(() => import('./components/VoucherPayments'));
        return (
          <React.Suspense fallback={<div className="p-6">Loading voucher payments...</div>}>
            <VoucherPayments key={refreshKey} />
          </React.Suspense>
        );

      case 'salary-payments':
        const SalaryPayments = React.lazy(() => import('./components/SalaryPayments'));
        return (
          <React.Suspense fallback={<div className="p-6">Loading salary payments...</div>}>
            <SalaryPayments key={refreshKey} />
          </React.Suspense>
        );

      case 'surveyor-expenses':
        const SurveyorExpenses = React.lazy(() => import('./components/SurveyorExpenses'));
        return (
          <React.Suspense fallback={<div className="p-6">Loading surveyor expenses...</div>}>
            <SurveyorExpenses key={refreshKey} />
          </React.Suspense>
        );

      case 'expense-register':
        const ExpenseRegister = React.lazy(() => import('./components/ExpenseRegister'));
        return (
          <React.Suspense fallback={<div className="p-6">Loading expense register...</div>}>
            <ExpenseRegister key={refreshKey} />
          </React.Suspense>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Create New Button */}
      <div className="flex justify-end">
        <div className="relative" ref={dropdownRef}>
          <Button
            onClick={() => setIsCreateDropdownOpen(!isCreateDropdownOpen)}
            className="flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Create New</span>
            <ChevronDown className={`w-4 h-4 transition-transform ${isCreateDropdownOpen ? 'rotate-180' : ''}`} />
          </Button>

          {isCreateDropdownOpen && (
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg border border-gray-200 z-50 overflow-y-auto max-h-96">
              <div className="py-1">
                {createOptions.map((option) => {
                  const IconComponent = option.icon;
                  return (
                    <button
                      key={option.id}
                      onClick={() => handleCreateOption(option.id)}
                      className="w-full flex items-center space-x-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      <IconComponent className="w-4 h-4 text-gray-500" />
                      <span>{option.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6 overflow-x-auto">
            {tabs.map((tab) => {
              const IconComponent = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
                  }`}
                >
                  <IconComponent className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {renderTabContent()}
        </div>
      </div>

      {/* Tax Invoice Form Modal */}
      {isInvoiceFormOpen && (
        <TaxInvoiceForm
          isOpen={isInvoiceFormOpen}
          onClose={() => setIsInvoiceFormOpen(false)}
          onSave={handleInvoiceFormSave}
          invoiceType={invoiceFormType}
        />
      )}
    </div>
  );
};

export default Purchases;

